import { USE_OUT_OF_CORE_TILE_MANAGER, USE_WBOIT } from "../globals";
import { FrustumIntersector } from './FrustumIntersector';
import THREE from "three";
import { OutOfCoreTileManager } from './out-of-core-tile-manager/OutOfCoreTileManager.js';
import { StageNames } from './out-of-core-tile-manager/tasks/defaultStageConfiguration';
import { OtgLoader } from "../../file-loaders/main/OtgLoader";
import { RenderBatch } from "./RenderBatch";

/** @import { SpatialTree, TreeNodes } from "./SpatialTreeInterface" */
/** @import { RenderModel } from "./RenderModel" */
/** @import { FragmentList } from "../scene/FragmentList" */

/*
 * Keep these threeJs objects file local for performance reasons,
 * as these are changed frequently, so we keep expensive object creation minimal.
 */
const tmpBox = new THREE.Box3();
const tmpBox2 = new THREE.Box3();
const tmpVec = new THREE.Vector3();
const sqrt3 = Math.sqrt(3);

export class ModelIteratorBVH
{
  // _bvhRenderBatches is a sparse array of RenderBatches, each RenderBatch has a few fragments.
  // Only those elements in the array that have a RenderBatch are defined.
  /** @type {RenderBatch[]} */#bvhRenderBatches;

  // What is the containment state of this node, if known? Is either CONTAINMENT_UNKNOWN
  // or INTERSECTS or CONTAINS. If CONTAINS, we don't have to run the frustum cull
  // test, saving a few percent in speed.
  #bvhContainment;

  #bvhNodeQueue;
  #bvhNodeScreenspaceErrors;
  #bvhHead;
  #bvhTail;
  #bvhLIFO;
  #bvhPrioritizeScreenSize;
  #bvhOpaqueDone;

  /** @type {OutOfCoreTileManager|undefined} */#outOfCoreTileManager;

  // true if nodes from transparent tree should be ignored for rendering.
  #bvhIgnoreTransparency;

  #frustum;
  #done;
  #RenderBatch;
  #resetVisStatus;
  #wantOoctm;

  // With #raycastOnly, the constructor will not initialize the RenderBatches. Since the
  // inner nodes are empty, bboxes of the RenderBatches and the bvh would be the same. Also,
  // the hidden bounding box and every other property of the RenderBatches wouldn't be used, wasting memory.
  // With this enabled, only rayCast and updateBoundsRaycastOnly are supported.
  /** @type {boolean} */#raycastOnly;

  /** @type {?RenderModel} */#renderModel;

  /** @type {?number} */#modelIteratorId;

  /** This factor is multiplied to the screen space error that is reported to the BVH iterator. At the moment, we use
   *  only the viewport size as scaling factor */
  #screenSpaceErrorScalingFactor = 1.0;

  /** @type {?SpatialTree} */#bvh;


  /**
   * Initializes the model iterator with the given parameters.
   * @param {RenderModel} renderModel - The render model for which this iterator is being created
   * @param {SpatialTree} bvh - See SpatialTreeInterface.js
   * @param {boolean} [initializeOoctm=true] - Whether to initialize the OutOfCoreTileManager. Should be set for non-consolidated BVHs.
   * @param {boolean} [raycastOnly=false] - Whether the iterator will be used only for raycasting.
   */
  constructor(renderModel, bvh, initializeOoctm = true, raycastOnly = false) {
    this.#bvh = bvh;
    this.#renderModel = renderModel;
    this.#raycastOnly = raycastOnly;
    if (this.#raycastOnly) {
      return;
    }
    this.#bvhHead = 0;
    this.#bvhTail = 0;
    this.#bvhLIFO = 1;
    this.#bvhOpaqueDone = false;
    this.#bvhIgnoreTransparency = false;
    this.#frustum = null;
    this.#done = false;
    this.#resetVisStatus = true;
    this.#modelIteratorId = null;

    // Take the RenderBatch class from the model, so on demand loading can
    // use a different class to handle redraws properly
    this.#RenderBatch = renderModel.RenderBatch;
    this.#wantOoctm = USE_OUT_OF_CORE_TILE_MANAGER && initializeOoctm;

    this.#bvhPrioritizeScreenSize = true;
    if (this.#bvh.options && this.#bvh.options.hasOwnProperty("prioritize_screen_size")) {
      this.#bvhPrioritizeScreenSize = this.#bvh.options.prioritize_screen_size;
    }

    const nodes = bvh.nodes;

    this.#bvhRenderBatches = new Array(nodes.nodeCount);
    this.#bvhContainment = new Int8Array(nodes.nodeCount);
    this.#bvhNodeQueue = new Int32Array(nodes.nodeCount + 1);
    this.#bvhNodeScreenspaceErrors = new Float32Array(nodes.nodeCount);

    for (let i = 0; i < nodes.nodeCount; i++) {

      // does this node have real objects in it?
      const primCount = nodes.getPrimCount(i);
      if (primCount === 0) {
        continue;
      }

      const largestFragmentSize = nodes.getLargestPrimitiveSize(i);
      const frags = this.#renderModel.getFragmentList();
      this.#bvhRenderBatches[i] = new this.#RenderBatch(this, frags, nodes.getPrimStart(i), primCount, undefined, largestFragmentSize, this.#bvh.options?.sortFuncType);

      const currentRenderBatch = this.#bvhRenderBatches[i];

      // These are set manually, because we will not be adding fragments to the
      // render batch one by one -- the fragments are already loaded.
      currentRenderBatch.lastItem = currentRenderBatch.start + primCount;
      currentRenderBatch.numAdded = primCount;
      currentRenderBatch.numAddedTotal = primCount;
      currentRenderBatch.nodeIndex = i;

      if (nodes.isNodeTransparent(i)) {
        currentRenderBatch.sortObjects = true; //scene contains transparent objects
      }

      nodes.getBoxArray(i, currentRenderBatch.bboxes);
    }
  }

  dtor() {
    this.#RenderBatch = null;
    this.#renderModel = null;

    this.#outOfCoreTileManager?.releaseIterator(this.#modelIteratorId);
  }

  unregisterOutOfCoreTileManager() {
    this.#outOfCoreTileManager = undefined;
  }

  /**
   * Reset the iterator for the given frustum and camera.
   * @param {FrustumIntersector} frustum - The frustum
   * @param {THREE.Camera} camera - The camera
   */
  reset(frustum, camera) {
    this.#frustum = frustum;

    // We scale the errors from the BVH iterator by the projection scale of the frustum
    // to take the size of the viewport into account when comparing the errors from different
    // viewports with each other in the OutOfCoreTileManager.
    // TODO: How should we handle the case when the camera is orthographic? We don't compute
    // a real orthographic screen space error, but divide by distance also in orthographic
    // views, because we consider closer objects more important, even if they do not occupy more
    // pixels. However, this poses a problem when comparing errors from orthographic and perspective
    // views because the errors for the orthographic view do not correspond to pixel sizes at all.
    // The projection factor used here, only gives the correct result for objects at a distance of 1
    // from the camera. We can use a different reference distance for orthographic views by scaling the error,
    // but it is unclear which reference distance we should use for this.
    this.#screenSpaceErrorScalingFactor = frustum.projScale;

    this.#bvhHead = 0;
    this.#bvhTail = 0;

    // means "unknown containment state"
    this.#bvhContainment[0] = this.#bvhContainment[1] = FrustumIntersector.CONTAINMENT_UNKNOWN;

    // prime the pump: the first entry is set to BVH node 0,
    // which is the first node in the first hierarchy (the opaque one) that we'll examine.
    // The ++ here is just for consistency; we could have set tail to 1
    // and used 0 as the index. _bvhTail will immediately get decremented to 0 by nextBatch;
    // it's incremented here to initially pass the while() loop there.
    if (USE_WBOIT) {
      // Also add the transparent sub-tree for WBOIT
      this.#bvhNodeQueue[this.#bvhTail++] = 1;
    }
    this.#bvhNodeQueue[this.#bvhTail++] = 0;

    this.#bvhOpaqueDone = false;
    this.#done = false;

    if (this.#resetVisStatus) {
      let scenes = this.#bvhRenderBatches;
      let len = scenes.length;
      for (let i = 0; i < len; ++i) {
        let scene = scenes[i];
        if (scene && scene.resetVisStatus) {
          scene.resetVisStatus();
        }
      }
      this.#resetVisStatus = false;
    }

    // The out of core manager maintains a counter for the current frame (full frame, not each individual pr
    // progressive frame) to determine whether an update of the screen space error is still valid. We increment
    // this counter here every time the traversal is being reset.
    this.#outOfCoreTileManager?.incrementFrameCount(this.#modelIteratorId);

    // Initialize the out of core tile manager and compute initial screen space errors to have proper task prioritization
    // This is done here lazily, because before the first reset there's no frustum and thus no useful priorities
    if (this.#wantOoctm && !this.#outOfCoreTileManager) {
      this.#outOfCoreTileManager = OutOfCoreTileManager.getManagerForModel(this.#renderModel);
      this.#modelIteratorId = this.#outOfCoreTileManager.getIteratorId(this);

      this.#bvhRenderBatches.forEach((renderBatch, index) => {
        const isTransparent = this.#bvh.nodes.isNodeTransparent(index);

        this.#outOfCoreTileManager.initializeBvhNode(index, isTransparent);
        this.updateNodeArea(index);
      });
    }

    // The root nodes (for opaques and transparent) are never added to the processing queue
    // and we never compute their screen area below. Therefore, we trigger the update already
    // here.
    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      this.updateNodeArea(0);
      this.updateNodeArea(1);
    }
  }

  resetTransparents() {
    this.#bvhHead = 0;
    this.#bvhTail = 0;
    // root of transparent subtree is at index 1
    this.#bvhNodeQueue[this.#bvhTail++] = 1;
  }

  // Used to insert nodes into the (sorted) render queue based on
  // a heuristic other than strict front to back or back to front order.
  // Currently we always use this for sorting by screen area.
  #insertNode(idx) {
    //This is basically a single sub-loop of an insertion sort.
    const val = this.#bvhNodeScreenspaceErrors[idx];
    let j = this.#bvhTail;

    if (this.#bvhLIFO) {
      // For LIFO we insert the largest at the end of the list, since they
      // are the first to be popped
      while (j > this.#bvhHead && this.#bvhNodeScreenspaceErrors[this.#bvhNodeQueue[j - 1]] > val) {
        this.#bvhNodeQueue[j] = this.#bvhNodeQueue[j - 1];
        j--;
      }
    } else {
      // For FIFO we insert the largest at the front of the list.
      while (j > this.#bvhHead && this.#bvhNodeScreenspaceErrors[this.#bvhNodeQueue[j - 1]] < val) {
        this.#bvhNodeQueue[j] = this.#bvhNodeQueue[j - 1];
        j--;
      }
    }

    this.#bvhNodeQueue[j] = idx;
    this.#bvhTail++;
  }

  /**
   * Check if traversal of the opaque subtree has finished and start
   * traversing the transparent subtree if appropriate.
   *
   * When using WBOIT, this should not be called.
   */
  _checkOpaqueCompletion() {
    if (!this.#bvhOpaqueDone && this.#bvhHead === this.#bvhTail) {
      // In case we want to skip all nodes in the transparent subtree.
      if (!this.#bvhIgnoreTransparency) {
        // If we are done with the opaque nodes, queue the transparent ones
        // before processing the contents of the last opaque node
        this.#bvhNodeQueue[this.#bvhTail++] = 1; //root of transparent subtree is at index 1
      }
      this.#bvhOpaqueDone = true;
    }
  }

  nextBatch() {
    if (!USE_WBOIT) {
      this._checkOpaqueCompletion();
    }

    // _bvhHead and _bvhTail are indices into the BVH node list. For the opaque objects
    // these start at 0 and 1, respectively. The idea here is to work through the bounding
    // volume hierarchy, with inner nodes sorted into the list by large-to-small screen area
    // (or front-to-back, or back-to-front) order as we go. The way this loop ends is when
    // nothing is on the BVH node stack, or a render batch of stuff to render is found.
    // The next time this method is called, the current _bvhHead and _bvhTail values pick
    // up where they left off, continuing to traverse the tree, until another batch is found
    // or the stack (list) is emptied.
    // Note: this is a breadth-first traversal, but render batches can and do get returned
    // before the whole tree is traversed, because these can be found in inner nodes.
    // This means that there may be nodes with larger screen areas that come later on.
    while (this.#bvhHead !== this.#bvhTail) {
      // Retrieve node index for what to process in the BVH. _bvhNodeQueue contains the indices
      // of the node(s) in the BVH that are to be processed.
      // For LIFO, for example, when the nodeIdx is first retrieved, _bvhTail initially
      // goes to 0, and so grabs the index at location 0 in _bvhNodeQueue, typically the top of
      // the opaque tree. The rest of this loop may add to this queue, and/or return fragments to
      // render, in which case it exits. If nothing got returned (yet) and the loop continues,
      // the next time around through this loop, the last
      // BVH node put on this _bvhNodeQueue stack (if LIFO is true) is retrieved (if not LIFO,
      // the first object on the list is retrieved and _bvhHead is incremented).
      // Inner nodes will add their two children in proper order to _bvhNodeQueue and increment _bvhTail, twice.
      const nodeIdx = this.#bvhLIFO || this.#bvhOpaqueDone ? this.#bvhNodeQueue[--this.#bvhTail] : this.#bvhNodeQueue[this.#bvhHead++];

      // Is box already found to be contained? This happens when a box's parent is fully contained.
      // We can then avoid the frustum test.
      let intersects = this.#bvhContainment[nodeIdx];
      if (intersects !== FrustumIntersector.CONTAINS) {
        // could be outside or intersecting, so do test
        this.#bvh.nodes.getBoxThree(nodeIdx, tmpBox);
        intersects = this.#frustum.intersectsBox(tmpBox);
      }

      // Node is entirely outside, go on to the next node
      if (intersects !== FrustumIntersector.OUTSIDE) {
        const childCount = this.#bvh.nodes.getChildCount(nodeIdx);

        // Is it inner node? Add children for processing.
        if (childCount > 0) {
          // For opaque objects, use the screen size to sort the children,
          // or front to back order (back to front for transparent objects).
          if (USE_WBOIT ||
          this.#bvhPrioritizeScreenSize && !this.#bvhOpaqueDone)
          {
            // Traversal based on visible screen area: compute area for each child
            // and insert into the priority queue. The heap maintains ordering.
            // Also used for N-ary trees where view-direction-based ordering is
            // not available (no split-axis flags).
            for (let childIdx = 0; childIdx < childCount; childIdx++) {
              const childNodeIdx = this.#bvh.nodes.getChildIndex(nodeIdx, childIdx);
              let area;

              // When using HLOD (without out of core) we currently use hlod traversal with the initial unconsolidated BVH. Its bvh options doesn't
              // have the sortFuncType and therefore falls back to the box area one.
              // By checking for this flag, we avoid using the hlod traversal for non-hlod bvhs
              if (USE_OUT_OF_CORE_TILE_MANAGER && this.#bvh.options.sortFuncType) {
                area = this.updateNodeArea(childNodeIdx);
              } else {
                this.#bvh.nodes.getBoxThree(childNodeIdx, tmpBox);
                area = this.#frustum.projectedBoxArea(tmpBox, intersects === FrustumIntersector.CONTAINS);
                this.#bvhNodeScreenspaceErrors[childNodeIdx] = area;
              }

              // "worst case" containment is recorded for later examination.
              this.#bvhContainment[childNodeIdx] = intersects;

              // Insert each node in the right place based on screen area,
              // so that the queue (or stack, if LIFO traversal) is kept sorted at every step of the way.
              // Note that with LIFO, for example, the larger object is put last on the list (a stack),
              // since we want to pop this one off first.
              if (area > 0) {
                this.#insertNode(childNodeIdx);
              }

            }

          } else {

            if (!this.#bvh.isBinaryTree) {
              throw new Error("View-direction traversal is not yet supported for non-binary trees.");
            }

            // Binary tree: traversal by view direction.
            // Reverse order if looking in the negative of the child split axis.
            // Reverse order if we are traversing last first.
            // If node contains transparent objects, then reverse the result so we traverse back to front.
            // In other words, reverse the order if an odd number of flags are true.
            const flags = this.#bvh.nodes.getFlags(nodeIdx);
            const reverseAxis = this.#frustum.viewDir[flags & 3] < 0 ? 1 : 0;
            let firstChild = flags >> 2 & 1;
            const transparent = flags >> 3 & 1;
            const depthFirst = this.#bvhLIFO || this.#bvhOpaqueDone ? 1 : 0;

            if (reverseAxis ^ depthFirst ^ transparent) {
              firstChild = 1 - firstChild;
            }

            const child = this.#bvh.nodes.getLeftChild(nodeIdx);
            const firstIdx = child + firstChild;
            const secondIdx = child + (1 - firstChild);

            this.#bvhNodeQueue[this.#bvhTail++] = firstIdx;
            this.#bvhNodeQueue[this.#bvhTail++] = secondIdx;

            // "worst case" containment is recorded for later examination.
            this.#bvhContainment[firstIdx] = this.#bvhContainment[secondIdx] = intersects;

            // Update the cost for transparent nodes. At the moment, we only use the distance to the
            // camera to determine the cost of the node, because nodes are rendered in back-to-front order
            // and thus distant nodes are more important.
            if (this.#outOfCoreTileManager) {
              this.updateNodeArea(firstIdx);
              this.updateNodeArea(secondIdx);
            } else {
              this.#bvhNodeScreenspaceErrors[firstIdx] = -1;
              this.#bvhNodeScreenspaceErrors[secondIdx] = -1;
            }
          }
        }

        // Are there graphics in the node? Then return its scene, i.e. its RenderBatch.
        // Inner nodes with children can and do have render batches of their own.
        // This works against a pure screen=area or front-to-back ordering, as
        // these fragments will always get returned first, before further traversal of the tree.
        const prims = this.#bvh.nodes.getPrimCount(nodeIdx);
        if (prims !== 0) {
          const renderBatch = this.#bvhRenderBatches[nodeIdx];

          if (!USE_WBOIT && renderBatch.sortObjects) {// Transparent nodes
            // We use a negative cost for transparent node to make sure they come after all opaque nodes
            // in the queue. The cost is the inverse of the distance to the camera, because we want to render
            // transparent nodes in back-to-front order.
            let distance = renderBatch.getBoundingBox(tmpBox).getBoundingSphere().center.distanceTo(this.#frustum.eye);
            renderBatch.renderImportance = -(1 / distance);
          } else {
            if (USE_OUT_OF_CORE_TILE_MANAGER) {
              // If the out of core tile manager is used, the nodes will be sorted based on the the node area
              // and we should use the same measure for the render importance that is used to decide which
              // batch to use next when doing multi-model rendering.
              renderBatch.renderImportance = this.#bvhNodeScreenspaceErrors[nodeIdx];
            } else {
              renderBatch.renderImportance = this.#frustum.projectedBoxArea(renderBatch.getBoundingBox(), intersects === FrustumIntersector.CONTAINS);
            }
          }

          // Frustum culling for the RenderBatch is done in RenderBatch.applyVisibility,
          // so we don't need it here.
          // Just returning the batch and it will get cull checked later.
          return renderBatch;
        }
      }

      if (!USE_WBOIT) {
        this._checkOpaqueCompletion();
      }
    }

    this.#done = true;
    return null;
  }

  /**
   * Updates the node area for an opaque node
   * @param {number} index - Index of the node
   * @returns {number} - area of the node or -1 if area could not be computed
   */
  updateNodeArea(index) {
    let area;
    let renderBatch = this.#bvhRenderBatches[index];

    // Skip empty nodes that don't have a render batch
    if (!renderBatch) {
      return -1;
    }


    if (this.#bvh.options.sortFuncType === Autodesk.Viewing.Private.BVHSortFuncType.DIAMETER) {
      // Use the bounding sphere radius of the largest fragment in RenderBatch divided by the distance to the RenderBatch bounding box
      const largestFragDiameterSquared = this.#bvh.nodes.getLargestPrimitiveSize(index);
      const largestFragRadius = Math.sqrt(largestFragDiameterSquared) * 0.5;
      area = largestFragRadius;

      if (this.#frustum.perspective) {
        this.#bvh.nodes.getBoxThree(index, tmpBox);

        // We clamp the distance to still order by largest object size if we are inside the bounding box
        area = largestFragRadius / Math.max(1, tmpBox.distanceToPoint(this.#frustum.eye));
      }
    } else {
      // Use the bounding box area of the largest fragment in RenderBatch divided by the squared distance to the RenderBatch bounding box
      area = this.#bvh.nodes.getLargestPrimitiveSize(index);

      if (this.#frustum.perspective) {
        this.#bvh.nodes.getBoxThree(index, tmpBox);
        let distSquared = tmpBox.distanceToPoint(this.#frustum.eye);
        distSquared *= distSquared;
        area = area / Math.max(1, distSquared);
      }
    }

    this.#bvhNodeScreenspaceErrors[index] = area;

    // This code path only updates the cost of the node in the out of core tile manager for
    // opaque nodes. Transparent nodes are treated separately in the nextBatch method.
    this.#outOfCoreTileManager?.updateBvhNodeScreenSpaceError(this.#modelIteratorId, index,
    this.#screenSpaceErrorScalingFactor * area);
    return area;
  }

  setIgnoreTransparency(ignore) {
    this.#bvhIgnoreTransparency = ignore;
  }

  /**
   * Updates the bounding box of a BVH node
   * @param {number} [nodeIdx] The ID of the node
   * @param {boolean} [updateChildren=true] - Also update the children of this node
   * @param {boolean} [updateParents=false] - Recursively update all the parents of this node
   *                                  (note this will not recompute the Bounding Box for the other children of the parents, but only make
   *                                   sure they contain the updated bounding box of this node)
   */
  #updateBVHRec(nodeIdx, updateChildren = true, updateParents = false) {
    const childCount = this.#bvh.nodes.getChildCount(nodeIdx);
    const hasChildren = childCount > 0;
    if (hasChildren && updateChildren) {
      for (let childIdx = 0; childIdx < childCount; childIdx++) {
        this.#updateBVHRec(this.#bvh.nodes.getChildIndex(nodeIdx, childIdx));
      }
    }

    // leaf nodes of raycast iterators don't need any update
    if (!this.#raycastOnly || hasChildren) {
      tmpBox.makeEmpty();

      if (hasChildren) {
        for (let childIdx = 0; childIdx < childCount; childIdx++) {
          this.#bvh.nodes.getBoxThree(this.#bvh.nodes.getChildIndex(nodeIdx, childIdx), tmpBox2);
          tmpBox.union(tmpBox2);
        }
      }

      if (!this.#raycastOnly && this.#bvh.nodes.getPrimCount(nodeIdx) > 0) {
        tmpBox.union(this.#bvhRenderBatches[nodeIdx].getBoundingBox());
        tmpBox.union(this.#bvhRenderBatches[nodeIdx].getBoundingBoxHidden());
      }

      this.#bvh.nodes.setBoxThree(nodeIdx, tmpBox);
    }

    const parent = this.#bvh.getNodeParent(nodeIdx);
    if (parent !== -1 && updateParents) {
      this.#updateBVHRec(parent, false, true);
    }
  }

  fragmentBoundsChanged(fragId) {
    const nodeIndex = this.#bvh.fragId2nodeId[fragId];
    this.#bvhRenderBatches[nodeIndex]?.invalidateBBox();
  }

  /**
   * Updates the bounds of a BVH node
   * @param {number} nodeIdx - The ID of the node
   * @param {boolean} forceUpdate - If false, we only update bounds for RenderBatches with bboxDirty = true
   */
  updateBounds(nodeIdx, forceUpdate = true) {
    this.#renderModel.iteratorChangedBounds();

    if (nodeIdx !== undefined) {
      this.#bvhRenderBatches[nodeIdx].updateBounds();
      this.#updateBVHRec(nodeIdx, false, true);
      return;
    }

    // Update the bounds of all render batches
    let isOtgLoader = this.#renderModel.loader instanceof OtgLoader;
    for (let i = 0; i < this.#bvhRenderBatches.length; i++) {
      let node = this.#outOfCoreTileManager?.getBvhNode(i);
      const renderBatch = this.#bvhRenderBatches[i];

      // With the OtgLoader, the fraglist might not be loaded yet, and until then the the bounds
      // of a node must not be updated because the fragment's bounding boxes are all zeroes.
      // So until the OOCTM nodes are initialized and the node's fraglist is loaded, we skip this.
      // We can safely skip this because LoadFraglistTask will call updateBounds() again.
      if (isOtgLoader && (this.#wantOoctm && !this.#outOfCoreTileManager ||
      node && !node.isStageCompleted(StageNames.FRAGLIST_LOADED))) {
        continue;
      }

      if (renderBatch) {
        const changed = renderBatch.updateBounds(forceUpdate);
        // Propagate change upwards for selective update. For forced update,
        // we skip this and just traverse the whole tree once after the loop.
        if (!forceUpdate && changed) {
          this.#updateBVHRec(i, false, true);
        }
      }
    }

    if (forceUpdate) {
      //Also update all bounding volume tree nodes' bounds.
      //If objects move too much this will make the BVH less effective.
      //However, this only happens during explode or animation, so it shouldn't
      //be an issue. We can always rebuild the BVH in case objects really move a lot.
      this.#updateBVHRec(0); //opaque root
      this.#updateBVHRec(1); //transparent root
    }
  }

  updateBoundsRaycastOnly() {
    console.assert(this.#raycastOnly, "updateBoundsRaycastOnly can only be called if raycastOnly is true");
    const frags = this.#renderModel.getFragmentList();
    const primArray = this.#bvh.primitives;

    for (let i = 0; i < this.#bvh.nodes.nodeCount; i++) {
      if (this.#bvh.nodes.getPrimCount(i) === 0) {
        continue;
      }

      const start = this.#bvh.nodes.getPrimStart(i);
      const end = start + this.#bvh.nodes.getPrimCount(i);
      tmpBox.makeEmpty();

      for (let j = start; j < end; j++) {
        const fragId = primArray[j];
        frags.getWorldBounds(fragId, tmpBox2);
        if (frags.isLine(fragId)) {
          // Expand bounding box a bit, to take into account axis aligned lines
          tmpBox2.expandByScalar(0.5);
        }
        tmpBox.union(tmpBox2);
      }
      this.#bvh.nodes.setBoxThree(i, tmpBox);
    }

    // update all bounding volume tree nodes' bounds.
    this.#updateBVHRec(0); // opaque root
    this.#updateBVHRec(1); // transparent root
  }


  getBounds(visibleBounds, hiddenBounds) {
    for (const batch of this.#bvhRenderBatches) {
      if (!batch) {
        continue;
      }

      visibleBounds.union(batch.getBoundingBox());
      hiddenBounds.union(batch.getBoundingBoxHidden());
    }
  }

  rayCast(raycaster, intersectsOut, dbIdFilter, options = {}) {
    const rayOrigin = raycaster.ray.origin;
    let nodeStack = [1, 0];
    let pt = new THREE.Vector3();
    let nodeBBox = new THREE.Box3();

    let currentRenderBatch;
    if (this.#raycastOnly) {
      // To still be able to re-use RenderBatch.raycast, this scratch RenderBatch is used.
      currentRenderBatch = new RenderBatch(null, this.#renderModel.getFragmentList(), -1, -1, this.#bvh.primitives);
      currentRenderBatch.bboxes = null;
    }

    // TODO when only the nearest hit is needed (no intersectsOut),
    // we can be much more efficient than iterating through all nodes.
    while (nodeStack.length > 0) {
      const nodeIdx = nodeStack.pop();

      this.#bvh.nodes.getBoxThree(nodeIdx, nodeBBox);

      if (options.maxDistance != undefined) {
        const distanceToPoint = nodeBBox.distanceToPoint(rayOrigin);
        if (distanceToPoint > options.maxDistance) {
          continue;
        }
      }

      const intersectionPoint = raycaster.ray.intersectBox(nodeBBox, pt);
      if (intersectionPoint === null) {
        continue;
      }

      const childCount = this.#bvh.nodes.getChildCount(nodeIdx);
      for (let childIdx = 0; childIdx < childCount; childIdx++) {
        nodeStack.push(this.#bvh.nodes.getChildIndex(nodeIdx, childIdx));
      }

      const prims = this.#bvh.nodes.getPrimCount(nodeIdx);
      if (prims !== 0) {
        if (this.#raycastOnly) {
          currentRenderBatch.start = this.#bvh.nodes.getPrimStart(nodeIdx);
          currentRenderBatch.lastItem = currentRenderBatch.start + this.#bvh.nodes.getPrimCount(nodeIdx);
        } else {
          currentRenderBatch = this.#bvhRenderBatches[nodeIdx];
        }
        currentRenderBatch.raycast(raycaster, intersectsOut, dbIdFilter, options);
      }
    }
  }

  intersectFrustum(frustumIntersector, callback) {
    let nodeStack = [1, FrustumIntersector.CONTAINMENT_UNKNOWN, 0, FrustumIntersector.CONTAINMENT_UNKNOWN];

    while (nodeStack.length) {

      const parentIntersectionState = nodeStack.pop();
      const nodeIdx = nodeStack.pop();

      //Check if current BVH node intersects the frustum. Take into account
      //the intersection state of the parent node, in case we can short-circuit the frustum check
      //when containment is known.
      let result;
      if (parentIntersectionState === FrustumIntersector.CONTAINS) {
        result = FrustumIntersector.CONTAINS;
      } else {
        this.#bvh.nodes.getBoxThree(nodeIdx, tmpBox);
        result = frustumIntersector.intersectsBox(tmpBox);
      }

      if (result === FrustumIntersector.OUTSIDE) {
        continue;
      }

      const childCount = this.#bvh.nodes.getChildCount(nodeIdx);
      for (let childIdx = 0; childIdx < childCount; childIdx++) {
        nodeStack.push(this.#bvh.nodes.getChildIndex(nodeIdx, childIdx));
        nodeStack.push(result);
      }

      const prims = this.#bvh.nodes.getPrimCount(nodeIdx);
      if (prims !== 0) {
        let scene = this.#bvhRenderBatches[nodeIdx];
        scene && scene.intersectFrustum(frustumIntersector, callback, result === FrustumIntersector.CONTAINS);
      }
    }
  }

  getSceneCount() {
    return this.#bvhRenderBatches.length;
  }

  getGeomScenes() {
    return this.#bvhRenderBatches;
  }

  getOutOfCoreTileManager() {
    return this.#outOfCoreTileManager;
  }

  getBVH() {
    return this.#bvh;
  }

  getFragOrder() {
    return this.#bvh.primitives;
  }

  done() {
    // not supported (yet)
    // Seems to be needed for search in gui/controls only. let's check if this information is required to be public.
    return this.#done;
  }

  resetVisStatus() {
    this.#resetVisStatus = true;
  }

  clone() {
    const bvhCopy = this.#bvh.createSortableReference();
    return new ModelIteratorBVH(this.#renderModel, bvhCopy, this.#wantOoctm);
  }

  /**
   * Return the ID which identifies the model iterator in the out of core tile manager.
   * @return {number} the id
   */
  getIteratorOutOfCoreManagerId() {
    return this.#modelIteratorId;
  }


  /**
   * @returns {boolean} true, if the OutOfCoreTileManager is supposed to be used.
   */
  wantOoctm() {
    return this.#wantOoctm;
  }

  /**
   * @param {number} fragId
   * @returns {RenderBatch|undefined}
   */
  getFragBatch(fragId) {
    const nodeIndex = this.#bvh.fragId2nodeId[fragId];
    // Get the render batch for this node
    return this.#bvhRenderBatches[nodeIndex];
  }
};